import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { ICriterion } from "../../distribute-team-members/models/criterion";
import { HttpClient } from "@angular/common/http";
import { MemberModel } from "../models/member.model";
import { CriterionInformation, MemberCriterionInformation } from "../models/criterion-information.mode";
import { Summary } from "../models/summary.model";
import { PagingResponse } from "../models/paging-response.model";
import { ExecutivePlan } from "../models/executive-plan.model";
import { FinalReportMistakes } from "../models/final-report-mistakes.model";
import { FinalReportResponse } from "../models/final-report-response.model";
import { FormioAppConfig } from "dp-formio";
import { PAGE_SIZE } from "./criterions.service";

@Injectable({ providedIn: 'root' })
export class ItimadCriterionsService {
    private baseUrl = this.formioAppConfig.gateway;

    constructor(private formioAppConfig: FormioAppConfig,
        private http: HttpClient) {
    }

    get criterionPath() {
        return this.formioAppConfig.endpoints.criterion;
    }
    get finalReportMistakes() {
        const path = this.formioAppConfig.endpoints.finalReportMistakes;
        return path;
    }
    get finalReportResponses() {
        const path = this.formioAppConfig.endpoints.finalReportResponses;
        return path;
    }
    private criterionInformationsEndpoint(isFinal: boolean = false) {
        const path = isFinal ?
            this.formioAppConfig.endpoints.finalCriterionInformations :
            this.formioAppConfig.endpoints.criterionInformations;
        return path;
    }

    getAllCriterionByVisitKey(visitKey: string): Observable<ICriterion[]> {
        const url = `${this.baseUrl}${this.criterionPath}visit/${visitKey}/members`;
        return this.http.get<any>(url, {
            params: {
                page: '0',
                size: `${Number.MAX_SAFE_INTEGER}`,
            },
        }).pipe(
            map(data => {
                data.forEach(item => {
                    if (item.numberOfApplicable > 0)
                        item.calculatedSavedScore = item.totalEvaluationCriteria / item.numberOfApplicable;
                });
                return data;
            })
        );
    }

    getMyCriterionByVisitKey(visitKey: string, reviewerId): Observable<ICriterion[]> {
        const url = `${this.baseUrl}${this.criterionPath}visit/${visitKey}/members/reviewer/${reviewerId}`;
        return this.http.get<any>(url, {
            params: {
                page: '0',
                size: `${Number.MAX_SAFE_INTEGER}`,
            },
        }).pipe(
            map(data => {
                data.forEach(item => {
                    if (item.numberOfApplicable > 0)
                        item.calculatedSavedScore = item.totalEvaluationCriteria / item.numberOfApplicable;
                });
                return data;
            })
        );
    }

    //summary filter
    getStickQuestionByVisitKey(visitKey: string): Observable<ICriterion[]> {
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.criterion}STICK/visit/${visitKey}`;
        return this.http.get<any>(url);
    }

    //summary filter
    getMembersByVisitKey(visitKey: string): Observable<MemberModel[]> {
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.visitMembers}visit/${visitKey}`;
        return this.http
            .get<any>(url, {
                params: {
                    page: '0',
                    size: `${Number.MAX_SAFE_INTEGER}`,
                },
            });
    }

    //summary filter
    getCriterionInformationForAll(params: any): Observable<PagingResponse<MemberCriterionInformation>> {
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.criterionInformations}visit/summaries`;
        return this.http.get<any>(url, {
            observe: 'response',
            params: params
        }).pipe(
            map((response) => {
                return {
                    totalItems: Number(response.headers.get('x-total-count')),
                    data: response.body,
                    size: params?.size ?? PAGE_SIZE,
                    page: params?.page ?? 0,
                };
            })
        );
    }

    getCriterionInformationForMainCriterion(visitKey: string, criterionKey: string, memberId: string, isFinal: boolean = false): Observable<CriterionInformation> {
        let url = `${this.baseUrl}${this.criterionInformationsEndpoint(isFinal)}${criterionKey}/visit/${visitKey}`;
        if (!isFinal) url += `/member/${memberId}`;
        return this.http.get<CriterionInformation>(url);
    }

    getCriterionInformation(visitKey: string, criterionKey: string, memberId: string, isFinal: boolean = false): Observable<CriterionInformation> {
        const url = `${this.baseUrl}${this.criterionInformationsEndpoint(isFinal)}`;
        const params: any = {
            'visitKey.equals': visitKey,
            'criterionKey.equals': criterionKey
        };
        if (!isFinal) params['memberId.equals'] = memberId;

        return this.http.get<CriterionInformation[]>(url, {
            params: params
        }).pipe(map(data => data.length ? data[0] : null));
    }

    createCriterionInformation(payload: CriterionInformation, isFinal: boolean = false): Observable<CriterionInformation> {
        const url = `${this.baseUrl}${this.criterionInformationsEndpoint(isFinal)}`;
        return this.http.post<CriterionInformation>(url, payload);
    }

    editCriterionInformation(payload: CriterionInformation, isFinal: boolean = false): Observable<CriterionInformation> {
        const url = `${this.baseUrl}${this.criterionInformationsEndpoint(isFinal)}`;
        return this.http.put<CriterionInformation>(url, payload);
    }

    getSummaries(visitKey: string, type: string, isCustom: boolean = false): Observable<Summary[]> {
        // v1 for get summaryOfRecommendations with finalReportResponseId
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.reportingservice}${isCustom ? 'v1/' : ''}${type}`;
        return this.http.get<Summary[]>(url, {
            params: {
                'visitKey.equals': visitKey,
                'deleted.equals': 'false'
            },
            responseType: 'json'
        });
    }

    saveSummaries(type: string, payload: Summary[]): Observable<Summary[]> {
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.reportingservice}v1/${type}`;
        return this.http.post<Summary[]>(url, payload);
    }

    getExecutivePlan(visitKey: string): Observable<ExecutivePlan[]> {
        const path = this.formioAppConfig.endpoints.executivePlan;
        const pathSegments = path.split('api/');
        const executivePlan = pathSegments[0] + `api/` + pathSegments[1];

        const url = `${this.baseUrl}${executivePlan}`;
        return this.http.get<any>(url, {
            params: {
                'visitKey.equals': visitKey,
                'deleted.equals': 'false'
            }
        });
    }

    saveExecutivePlan(payload: ExecutivePlan[]): Observable<ExecutivePlan[]> {
        const path = this.formioAppConfig.endpoints.customExecutivePlan;
        const pathSegments = path.split('api/v1/');
        const executivePlan = pathSegments[0] + `api/v1/` + pathSegments[1];

        const url = `${this.baseUrl}${executivePlan}`;
        return this.http.post<ExecutivePlan[]>(url, payload);
    }

    getFinalReportMistakes(visitKey: string, criterionKey: string): Observable<FinalReportMistakes> {
        const url = `${this.baseUrl}${this.finalReportMistakes}`;
        return this.http.get<FinalReportMistakes[]>(url, {
            params: {
                'visitKey.equals': visitKey,
                'criterionKey.equals': criterionKey
            }
        }).pipe(map(data => data.length ? data[0] : null));;
    }

    createFinalReportMistakes(payload: FinalReportMistakes): Observable<FinalReportMistakes> {
        const url = `${this.baseUrl}${this.finalReportMistakes}`;
        return this.http.post<FinalReportMistakes>(url, payload);
    }

    editFinalReportMistakes(payload: FinalReportMistakes): Observable<FinalReportMistakes> {
        const url = `${this.baseUrl}${this.finalReportMistakes}`;
        return this.http.put<FinalReportMistakes>(url, payload);
    }

    deleteFinalReportMistakes(id: number): Observable<FinalReportMistakes> {
        const url = `${this.baseUrl}${this.finalReportMistakes}/${id}`;
        return this.http.delete<FinalReportMistakes>(url);
    }

    getFinalReportResponses(id: string): Observable<FinalReportResponse> {
        const url = `${this.baseUrl}${this.finalReportResponses}${id}`;
        return this.http.get<FinalReportResponse>(url);
    }

    createFinalReportResponses(payload: FinalReportResponse): Observable<FinalReportResponse> {
        const url = `${this.baseUrl}${this.finalReportResponses}`;
        return this.http.post<FinalReportResponse>(url, payload);
    }

    editFinalReportResponses(payload: FinalReportResponse): Observable<FinalReportResponse> {
        const url = `${this.baseUrl}${this.finalReportResponses}`;
        return this.http.put<FinalReportResponse>(url, payload);
    }
}

